<template>
  <div class="table-container">
    <!-- Fejléc és műveletek -->
    <div class="table-header" v-if="showHeader">
      <div class="table-title-section">
        <h3 class="table-title" v-if="title">{{ title }}</h3>
        <span class="table-count" v-if="showCount && totalCount">{{ totalCount }} elem</span>
        <span class="selection-count" v-if="selectable && selectedItems.length > 0">
          {{ selectedItems.length }} kiválasztva
        </span>
      </div>
      <div class="table-actions">
        <slot name="actions"></slot>
      </div>
    </div>

    <!-- Szűrők -->
    <div class="table-filters" v-if="$slots.filters">
      <slot name="filters"></slot>
    </div>

    <!-- Táblázat -->
    <div class="table-wrapper" :class="{ 'table-loading': loading }">
      <div class="table no-more-tables">
        <!-- Fejléc -->
        <div class="table-head">
          <div class="table-row" :style="selectableGridTemplateColumns">
            <!-- Select All Checkbox -->
            <div v-if="selectable" class="table-th checkbox-column">
              <input 
                type="checkbox" 
                class="form-check-input" 
                :checked="isAllSelected"
                :indeterminate.prop="isIndeterminate"
                @change="toggleSelectAll"
                :disabled="loading || !data || data.length === 0"
              />
            </div>

            <div v-for="column in columns" 
                 :key="column.key" 
                 :class="[
                   'table-th',
                   column.sortable ? 'sortable' : '',
                   column.align ? `text-${column.align}` : '',
                   column.width ? `width-${column.width}` : ''
                 ]"
                 @click="column.sortable ? handleSort(column.key) : null">
              {{ column.label }}
              <i v-if="column.sortable" 
                 :class="getSortIcon(column.key)"
                 class="sort-icon"></i>
            </div>
          </div>
        </div>

        <!-- Tartalom -->
        <div class="table-body">
          <!-- Loading állapot -->
          <div v-if="loading" class="table-loading-row">
            <div class="loading-content">
              <div class="spinner-border spinner-border-sm" role="status"></div>
              <span>{{ loadingText }}</span>
            </div>
          </div>
          
          <!-- Üres állapot -->
          <div v-else-if="!data || data.length === 0" class="table-empty-row">
            <div class="empty-content">
              <i :class="emptyIcon" class="empty-icon"></i>
              <div class="empty-text">{{ emptyText }}</div>
              <slot name="empty-actions"></slot>
            </div>
          </div>
          
          <!-- Adatok -->
          <div v-else 
               v-for="(item, index) in data" 
               :key="getRowKey(item, index)"
               :class="[
                 'table-row',
                 rowClass ? rowClass(item, index) : '',
                 clickableRows ? 'clickable' : '',
                 isItemSelected(item) ? 'selected' : ''
               ]"
               :style="selectableGridTemplateColumns"
               @click="handleRowClickWithSelection($event, item, index)">
            
            <!-- Checkbox Column -->
            <div v-if="selectable" class="table-cell checkbox-column" data-label="Kiválasztás">
              <input 
                type="checkbox" 
                class="form-check-input" 
                :checked="isItemSelected(item)"
                @change="toggleSelection(item)"
                @click.stop
              />
            </div>

            <div v-for="column in columns" 
                 :key="column.key"
                 :class="[
                   'table-cell',
                   column.align ? `text-${column.align}` : '',
                   column.cellClass ? column.cellClass(item) : ''
                 ]"
                 :data-label="column.label">
              <!-- Slot alapú megjelenítés -->
              <slot v-if="$slots[`cell-${column.key}`]" 
                    :name="`cell-${column.key}`" 
                    :item="item" 
                    :value="getNestedValue(item, column.key)"
                    :index="index">
              </slot>
              
              <!-- Formázott megjelenítés -->
              <span v-else-if="column.formatter" 
                    v-html="column.formatter(getNestedValue(item, column.key), item)">
              </span>
              
              <!-- Egyszerű megjelenítés -->
              <span v-else>{{ getNestedValue(item, column.key) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lapozás -->
    <div class="table-pagination" v-if="pagination && pagination.last_page > 1">
      <div class="pagination-info">
        {{ pagination.per_page * (pagination.current_page - 1) + 1 }}-{{ pagination.per_page * pagination.current_page }} / {{ pagination.total }} elem
      </div>
      
      <div class="pagination-controls">
        <button class="btn btn-sm btn-outline-primary" 
                :disabled="pagination.current_page <= 1"
                @click="handlePageChange(pagination.current_page - 1)">
          <i class="fas fa-chevron-left"></i>
        </button>
        
        <span class="page-info">
          {{ pagination.current_page }} / {{ pagination.last_page }}
        </span>
        
        <button class="btn btn-sm btn-outline-primary"
                :disabled="pagination.current_page >= pagination.last_page"
                @click="handlePageChange(pagination.current_page + 1)">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Table',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showCount: {
      type: Boolean,
      default: true
    },
    totalCount: {
      type: Number,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: 'Betöltés...'
    },
    emptyText: {
      type: String,
      default: 'Nincsenek adatok'
    },
    emptyIcon: {
      type: String,
      default: 'fas fa-inbox'
    },
    sortBy: {
      type: String,
      default: ''
    },
    sortDirection: {
      type: String,
      default: 'asc',
      validator: value => ['asc', 'desc'].includes(value)
    },
    pagination: {
      type: Object,
      default: null
    },
    rowKey: {
      type: [String, Function],
      default: 'id'
    },
    rowClass: {
      type: Function,
      default: null
    },
    clickableRows: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selectedItems: {
      type: Array,
      default: () => []
    }
  },
  
  computed: {
    gridTemplateColumns() {
      const columns = this.columns.map(col => {
        switch(col.width) {
          case 'xs': return 'minmax(80px, 0.5fr)'
          case 'sm': return 'minmax(120px, 1fr)'
          case 'md': return 'minmax(200px, 1.5fr)'
          case 'lg': return 'minmax(250px, 2fr)'
          case 'xl': return 'minmax(300px, 3fr)'
          default: return '1fr'
        }
      }).join(' ')
      
      return {
        gridTemplateColumns: columns
      }
    },

    selectableGridTemplateColumns() {
      if (!this.selectable) {
        return this.gridTemplateColumns
      }

      const checkboxColumn = '50px'
      const columns = this.columns.map(col => {
        switch(col.width) {
          case 'xs': return 'minmax(80px, 0.5fr)'
          case 'sm': return 'minmax(120px, 1fr)'
          case 'md': return 'minmax(200px, 1.5fr)'
          case 'lg': return 'minmax(250px, 2fr)'
          case 'xl': return 'minmax(300px, 3fr)'
          default: return '1fr'
        }
      }).join(' ')
      
      return {
        gridTemplateColumns: `${checkboxColumn} ${columns}`
      }
    },

    isAllSelected() {
      return this.data && this.data.length > 0 && 
             this.selectedItems.length === this.data.length
    },

    isIndeterminate() {
      return this.selectedItems.length > 0 && 
             this.selectedItems.length < this.data.length
    }
  },
  
  methods: {
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => current?.[key], obj)
    },
    
    getRowKey(item, index) {
      if (typeof this.rowKey === 'function') {
        return this.rowKey(item, index)
      }
      return this.getNestedValue(item, this.rowKey) || index
    },
    
    handleSort(key) {
      const newDirection = this.sortBy === key && this.sortDirection === 'asc' ? 'desc' : 'asc'
      this.$emit('sort', { key, direction: newDirection })
    },
    
    getSortIcon(key) {
      if (this.sortBy !== key) return 'fas fa-sort'
      return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
    },
    
    handleRowClickWithSelection(event, item, index) {
      // Ha checkbox-ra kattintottunk, ne csináljon semmit
      if (event.target.type === 'checkbox') {
        return
      }
      
      if (this.clickableRows) {
        this.handleRowClick(item, index)
      }
    },

    handleRowClick(item, index) {
      this.$emit('row-click', { item, index })
    },
    
    handlePageChange(page) {
      this.$emit('page-change', page)
    },

    isItemSelected(item) {
      const itemKey = this.getRowKey(item, 0)
      return this.selectedItems.some(selectedItem => 
        this.getRowKey(selectedItem, 0) === itemKey
      )
    },

    toggleSelection(item) {
      this.$emit('selection-change', item)
    },

    toggleSelectAll() {
      this.$emit('select-all-change', !this.isAllSelected)
    }
  }
}
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

/* Fejléc */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #495057;
  margin: 0;
}

.table-count {
  background: #1d4699;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

/* Szűrők */
.table-filters {
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

/* Táblázat */
.table-wrapper {
  overflow-x: auto;
  position: relative;
}

.table-loading {
  opacity: 0.7;
}

.table {
  font-size: 14px;
  width: 100%;
  display: block;
}

.table-head {
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  position: sticky;
  top: 0;
  z-index: 10;
}

.table-body {
  display: block;
}

.table-row {
  display: grid;
  align-items: center;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;
}

.table-body .table-row:nth-child(even) {
  background: #f8f9fa;
}

.table-body .table-row:hover {
  background: #e3f2fd !important;
}

.table-row.clickable {
  cursor: pointer;
}

.table-th {
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  /* white-space: nowrap; - eltávolítjuk */
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
  hyphens: auto;
  line-height: 1.4;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-th.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.table-th.sortable:hover {
  background: #e9ecef;
}

.sort-icon {
  margin-left: 8px;
  color: #6c757d;
  font-size: 12px;
  flex-shrink: 0;
}

.table-cell {
  padding: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  background: transparent;
}

/* Szöveg igazítás */
.text-left { text-align: left; display: block; }
.text-center { text-align: center; display: block; }
.text-right { text-align: right; display: block; }

/* Loading állapot */
.table-loading-row {
  padding: 40px;
  text-align: center;
  grid-column: 1 / -1;
}

.loading-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #6c757d;
}

/* Üres állapot */
.table-empty-row {
  padding: 60px 20px;
  text-align: center;
  grid-column: 1 / -1;
}

.empty-content {
  color: #6c757d;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.5;
}

.empty-text {
  font-size: 16px;
  margin-bottom: 20px;
}

/* Lapozás */
.table-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.pagination-info {
  color: #6c757d;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-info {
  color: #6c757d;
  font-size: 14px;
  min-width: 60px;
  text-align: center;
}

/* No More Tables responsive design */
@media (max-width: 768px) {
  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .table-pagination {
    flex-direction: column;
    gap: 15px;
  }

  /* Elrejtjük a fejlécet mobilon */
  .no-more-tables .table-head {
    display: none;
  }

  /* A sorok kártya megjelenítésűvé válnak */
  .no-more-tables .table-row {
    display: block;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    margin-bottom: 15px;
    padding: 15px;
    background: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    grid-template-columns: none !important;
  }

  .no-more-tables .table-row:nth-child(even) {
    background: white;
  }

  .no-more-tables .table-row:hover {
    background: #f8f9fa !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  /* Cellák függőleges elrendezése */
  .no-more-tables .table-cell {
    display: block;
    border: none;
    border-bottom: 1px solid #f1f3f4;
    padding: 8px 0;
    text-align: left !important;
    position: relative;
    padding-left: 120px;
    min-height: 30px;
  }

  .no-more-tables .table-cell:last-child {
    border-bottom: none;
  }

  /* Label megjelenítése mobilon */
  .no-more-tables .table-cell:before {
    content: attr(data-label) ":";
    position: absolute;
    left: 0;
    top: 8px;
    width: 110px;
    padding-right: 10px;
    white-space: nowrap;
    font-weight: 600;
    color: #495057;
    font-size: 13px;
  }

  /* Speciális esetek a loading és empty sorokra */
  .no-more-tables .table-loading-row,
  .no-more-tables .table-empty-row {
    display: block;
    padding: 40px 20px;
    text-align: center;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    margin-bottom: 15px;
    background: white;
  }

  /* Checkbox oszlop */
  .no-more-tables .checkbox-column {
    display: block;
    padding: 8px 0 !important;
    padding-left: 120px !important;
    border-bottom: 1px solid #f1f3f4;
  }

  .no-more-tables .checkbox-column:before {
    content: "Kiválasztás:";
    position: absolute;
    left: 0;
    top: 8px;
    width: 110px;
    padding-right: 10px;
    white-space: nowrap;
    font-weight: 600;
    color: #495057;
    font-size: 13px;
  }

  .no-more-tables .checkbox-column input[type="checkbox"] {
    margin: 0;
  }

  .selection-count {
    margin-top: 8px;
  }

  /* Kártya animáció */
  .no-more-tables .table-row {
    transition: all 0.3s ease;
  }

  /* Mobil specifikus spacing */
  .no-more-tables .table-body {
    padding: 0 5px;
  }
}

/* Tablet méret (opcionális átmeneti megjelenítés) */
@media (max-width: 1024px) and (min-width: 769px) {
  .table-th {
    padding: 12px 8px;
    font-size: 13px;
    min-height: 45px;
  }
  
  .table-cell {
    padding: 10px 8px;
    font-size: 13px;
  }
}

/* Nagy mobilok / kis tabletek */
@media (max-width: 480px) {
  .no-more-tables .table-cell:before {
    width: 90px;
    font-size: 12px;
  }

  .table-title {
    font-size: 16px;
  }

  .table-header {
    padding: 15px;
  }

  .table-pagination {
    padding: 15px;
  }
}

/* Checkbox oszlop */
.checkbox-column {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px !important;
}

.checkbox-column input[type="checkbox"] {
  cursor: pointer;
  width: 18px;
  height: 18px;
  margin: 0;
}

.checkbox-column input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Kiválasztott sor kiemelése */
.table-body .table-row.selected {
  background: #e3f2fd !important;
  border-left: 3px solid #1d4699;
}

.table-body .table-row.selected:hover {
  background: #bbdefb !important;
}

/* Kiválasztás számláló */
.selection-count {
  background: #4caf50;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}
</style>